import React from 'react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Users, Star, BarChart2, X, Circle as XCircle, CircleAlert as AlertCircle, Loader } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useUser } from '../context/UserContext';
import { VocalBlueprintModal } from '../components/VocalBlueprintModal';

const renderMarkdown = (text: string): JSX.Element => {
  const lines = text.split('\n');
  const elements: JSX.Element[] = [];
  let listItems: string[] = [];

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(
        <ul key={`list-${elements.length}`} className="list-disc list-inside space-y-1 my-2">
          {listItems.map((item, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: formatInlineMarkdown(item) }} />
          ))}
        </ul>
      );
      listItems = [];
    }
  };

  const formatInlineMarkdown = (line: string): string => {
    return line
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/`(.+?)`/g, '<code class="bg-white/10 px-1 rounded">$1</code>');
  };

  lines.forEach((line, index) => {
    if (line.startsWith('### ')) {
      flushList();
      elements.push(
        <h3 key={index} className="text-lg font-semibold text-kod-accent mt-4 mb-2">
          {line.substring(4)}
        </h3>
      );
    } else if (line.startsWith('## ')) {
      flushList();
      elements.push(
        <h2 key={index} className="text-xl font-bold text-kod-accent mt-4 mb-2">
          {line.substring(3)}
        </h2>
      );
    } else if (line.startsWith('# ')) {
      flushList();
      elements.push(
        <h1 key={index} className="text-2xl font-bold text-kod-accent mt-4 mb-2">
          {line.substring(2)}
        </h1>
      );
    } else if (line.match(/^[-*]\s+/)) {
      listItems.push(line.replace(/^[-*]\s+/, ''));
    } else if (line.trim() === '') {
      flushList();
      if (elements.length > 0) {
        elements.push(<div key={`space-${index}`} className="h-2" />);
      }
    } else {
      flushList();
      elements.push(
        <p key={index} className="my-1" dangerouslySetInnerHTML={{ __html: formatInlineMarkdown(line) }} />
      );
    }
  });

  flushList();

  return <>{elements}</>;
};

interface JocelynScoreProps {
  overallScore: number;
  pitchAccuracy?: number;
  rhythmAccuracy?: number;
  styleScore?: number;
  onClose?: () => void;
  songId?: string;
  songTitle?: string;
  songArtist?: string;
  userRecordingUrl?: string;
  vocalsOnlyUrl?: string | null;
  originalVocalUrl?: string;
  recordingId?: string;
  autoScoring?: boolean;
  uploadError?: string | null;
  scoringRequestFailed?: boolean;
  isTournament?: boolean;
}

export const JocelynScore: React.FC<JocelynScoreProps> = ({
  overallScore: initialOverallScore,
  pitchAccuracy: initialPitchAccuracy = 0,
  rhythmAccuracy: initialRhythmAccuracy = 0,
  styleScore: initialStyleScore = 0,
  onClose,
  songId,
  songTitle = "Your Song",
  songArtist = "Artist",
  userRecordingUrl,
  vocalsOnlyUrl,
  originalVocalUrl,
  recordingId,
  autoScoring = false,
  uploadError = null,
  scoringRequestFailed = false,
  isTournament = false
}) => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [performanceExists, setPerformanceExists] = useState(autoScoring);
  const [analysisExists, setAnalysisExists] = useState(false);
  const [performanceId, setPerformanceId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // State for actual score values from database
  const [overallScore, setOverallScore] = useState(initialOverallScore);
  const [pitchAccuracy, setPitchAccuracy] = useState(initialPitchAccuracy);
  const [rhythmAccuracy, setRhythmAccuracy] = useState(initialRhythmAccuracy);
  const [styleScore, setStyleScore] = useState(initialStyleScore);
  const [coachingResponse, setCoachingResponse] = useState<string>('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [analysisError, setAnalysisError] = useState<{errorCode: string; message: string} | null>(null);
  const [progressStep, setProgressStep] = useState(0);
  const [progressMsg, setProgressMsg] = useState('Uploading your take...');
  const [isCheckingExistingScore, setIsCheckingExistingScore] = useState(Boolean(recordingId));
  const [hasCheckedExistingScore, setHasCheckedExistingScore] = useState(false);

  const [blueprintModalOpen, setBlueprintModalOpen] = useState(false);
  const [reportCompressedData, setReportCompressedData] = useState<string | null>(null);
  const [lyricsUrl, setLyricsUrl] = useState<string | undefined>();
  const [leadVocalUrlForBlueprint, setLeadVocalUrlForBlueprint] = useState<string | undefined>();
  const [mixedRecordingUrl, setMixedRecordingUrl] = useState<string | undefined>();
  
  useEffect(() => {
    if (analysisExists || analysisError) return;

    setProgressStep(0);
    setProgressMsg('Uploading your take...');

    const timers: ReturnType<typeof setTimeout>[] = [];
    timers.push(setTimeout(() => { setProgressStep(1); setProgressMsg('Extracting your vocals...'); }, 3000));
    timers.push(setTimeout(() => { setProgressStep(2); setProgressMsg('Processing audio...'); }, 8000));
    timers.push(setTimeout(() => { setProgressStep(3); setProgressMsg('Scoring your performance...'); }, 20000));

    return () => timers.forEach(clearTimeout);
  }, [analysisExists, analysisError]);

  useEffect(() => {
    if (!uploadError) return;
    setAnalysisExists(true);
    setAnalysisError({ errorCode: 'UPLOAD_FAILED', message: uploadError });
  }, [uploadError]);

  useEffect(() => {
    if (!scoringRequestFailed) return;
    setAnalysisExists(true);
    setAnalysisError({
      errorCode: 'SUBMIT_ERROR',
      message: "We couldn't send your recording to Jocelyn for scoring. Tap Retry Scoring to try again."
    });
  }, [scoringRequestFailed]);

  const showRetry = Boolean(analysisError) && !uploadError && (!isTournament || analysisError?.errorCode === 'SUBMIT_ERROR');

  // Poll for performance and analysis records until analysis completes, errors, or times out
  useEffect(() => {
    if (!recordingId || !user?.id) {
      setIsCheckingExistingScore(false);
      return;
    }

    setIsCheckingExistingScore(true);
    setHasCheckedExistingScore(false);
    let cancelled = false;
    let interval: ReturnType<typeof setInterval> | null = null;
    let stepTimer: ReturnType<typeof setTimeout> | null = null;
    const started = Date.now();
    const maxMs = 180_000;

    const checkPerformanceStatus = async () => {
      if (cancelled) return;

      try {
        const { data: performance, error: performanceError } = await supabase
          .from('performances')
          .select('id')
          .eq('recording_id', recordingId)
          .order('created_at', { ascending: false })
          .limit(1);

        if (performanceError && performanceError.code !== 'PGRST116') {
          console.error('Error checking performance:', performanceError);
          return;
        }

        if (performance && performance.length > 0) {
          setPerformanceExists(true);
          setPerformanceId(performance[0].id);

          const { data: analysisRows, error: analysisErr } = await supabase
            .from('performance_analysis')
            .select('id, overall_score, pitch_accuracy, timing_accuracy, note_transition_score, coaching_response, status_code, error_code, error_message, report_compressed_b64')
            .eq('performance_id', performance[0].id)
            .order('created_at', { ascending: false })
            .limit(1);

          if (analysisErr) {
            console.error('Error checking analysis:', analysisErr);
            return;
          }

          const analysis = analysisRows && analysisRows.length > 0 ? analysisRows[0] : null;
          if (analysis) {
            setAnalysisExists(true);

            if (analysis.status_code >= 400) {
              setAnalysisError({
                errorCode: analysis.error_code || `HTTP_${analysis.status_code}`,
                message: analysis.error_message || 'Something went wrong while scoring your performance. You can try again below.'
              });
            } else {
              setAnalysisError(null);
              if (analysis.overall_score != null) setOverallScore(analysis.overall_score);
              if (analysis.pitch_accuracy != null) setPitchAccuracy(analysis.pitch_accuracy);
              if (analysis.timing_accuracy != null) setRhythmAccuracy(analysis.timing_accuracy);
              if (analysis.note_transition_score != null) setStyleScore(analysis.note_transition_score);
              if (analysis.coaching_response) setCoachingResponse(analysis.coaching_response);
              if (analysis.report_compressed_b64) setReportCompressedData(analysis.report_compressed_b64);
            }

            if (interval) clearInterval(interval);
            if (stepTimer) clearTimeout(stepTimer);
            return;
          }
        }

        if (Date.now() - started > maxMs) {
          if (interval) clearInterval(interval);
          if (stepTimer) clearTimeout(stepTimer);
          setAnalysisError({
            errorCode: 'TIMEOUT',
            message: 'Scoring is taking longer than expected. Your recording has been submitted — check back shortly for your results.'
          });
        }
      } catch (err) {
        console.error('Error checking performance status:', err);
      } finally {
        if (!cancelled) {
          setIsCheckingExistingScore(false);
          setHasCheckedExistingScore(true);
        }
      }
    };

    checkPerformanceStatus();
    interval = setInterval(checkPerformanceStatus, 2000);

    // Fetch song lyrics and recording mix once
    if (songId) {
      supabase
        .from('songs')
        .select('lyrics_url, lead_vocal_url')
        .eq('id', songId)
        .maybeSingle()
        .then(({ data: songData }) => {
          if (songData) {
            setLyricsUrl(songData.lyrics_url || undefined);
            setLeadVocalUrlForBlueprint(songData.lead_vocal_url || undefined);
          }
        });
    }

    if (recordingId) {
      supabase
        .from('recordings')
        .select('mixed_recording_url, final_video_url')
        .eq('id', recordingId)
        .maybeSingle()
        .then(({ data: recordingData }) => {
          if (recordingData) {
            setMixedRecordingUrl(recordingData.mixed_recording_url || recordingData.final_video_url || undefined);
          }
        });
    }

    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
      if (stepTimer) clearTimeout(stepTimer);
    };
  }, [recordingId, user?.id, songId]);
  
  const handleTryAnotherSong = () => {
    navigate('/audition-songs');
  };
  
  const handleScorePerformance = async () => {
    if (!songId || !user?.id || !recordingId) {
      setError("Required information is missing. Please try recording again.");
      return;
    }

    const audioUrlForScoring = vocalsOnlyUrl || userRecordingUrl;

    if (!audioUrlForScoring) {
      setError("Recording URL is missing. Please try recording again.");
      return;
    }

    try {
      setError(null);
      setIsSubmitting(true);

      const response = await fetch(`${supabase.supabaseUrl}/functions/v1/submit-score`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabase.supabaseKey}`
        },
        body: JSON.stringify({
          user_id: user.id,
          song_id: songId,
          VoiceReference: originalVocalUrl,
          VoiceRecording: audioUrlForScoring,
          recording_id: recordingId
        })
      });
      
      if (!response.ok) {
        throw new Error(`Scoring failed with status: ${response.status}`);
      }
      
      // Update state to show pending analysis
      setPerformanceExists(true);
      
    } catch (err) {
      console.error('Error submitting for scoring:', err);
      const msg = err instanceof Error ? err.message : 'Failed to submit for scoring';
      setAnalysisExists(true);
      setAnalysisError({
        errorCode: 'SUBMIT_ERROR',
        message: msg,
      });
      setIsSubmitting(false);
    }
  };
  
  // Live scoring stays visible throughout; only reopened scores wait for the first check to avoid a loading flash.
  if (!autoScoring && (isCheckingExistingScore || !hasCheckedExistingScore)) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-kod-purple-800 rounded-xl overflow-hidden max-w-md w-full shadow-xl relative">
        <div className={`relative ${analysisExists ? 'h-32' : 'h-28 bg-gradient-to-br from-kod-purple-900 via-kod-purple-800 to-kod-purple-900'}`}>
          {analysisExists && (
            <>
              <img
                src="https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80"
                alt="Background"
                className="w-full h-32 object-cover opacity-60"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-kod-purple-900/50 to-kod-purple-800"></div>
            </>
          )}

          {onClose && (
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-20 p-2 bg-kod-purple-700/80 hover:bg-kod-purple-600 text-white rounded-full transition-colors"
              aria-label="Close modal"
            >
              <XCircle size={24} />
            </button>
          )}

          <div className="absolute inset-0 flex flex-col items-center justify-center px-12">
            {!analysisExists && performanceExists && (
              <div className="flex gap-1.5 mb-3" aria-label="Scoring in progress">
                {[0, 1, 2].map((step) => (
                  <span
                    key={step}
                    className={`h-1.5 w-8 rounded-full transition-colors ${step <= progressStep ? 'bg-kod-accent' : 'bg-white/20'}`}
                  />
                ))}
              </div>
            )}
            <h1 className="text-2xl font-bold text-white text-center mb-1">
              {analysisError ? "Scoring Failed" :
               analysisExists ? "Your Audition Results Are In" :
               performanceExists ? "Analyzing Your Performance..." :
               "Ready to Get Your Score?"}
            </h1>
          </div>
        </div>
        
        <div className="p-6">
          <div className={`bg-kod-purple-700/50 rounded-lg p-4 mb-6 border border-kod-purple-600/40 ${isExpanded ? 'max-h-96 overflow-y-auto' : ''}`}>
            <div className="flex items-start">
              <img
                src="https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80"
                alt="Jocelyn"
                className="w-14 h-14 rounded-full object-cover border-2 border-kod-accent mr-3 flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-kod-accent mb-1">Jocelyn</h3>
                <div className="relative">
                  <div className={`text-white/90 text-sm ${!isExpanded && coachingResponse ? 'line-clamp-3' : ''}`}>
                    {uploadError
                      ? "Your recording didn't make it to me. Close this window and give it another go."
                      : analysisError
                      ? "Something went wrong while scoring your performance. You can tap 'Retry Scoring' below to try again, or close this and re-record."
                      : analysisExists && coachingResponse
                      ? renderMarkdown(coachingResponse)
                      : analysisExists
                      ? "Hey superstar, I've got your score right here — let's see how you did!"
                      : performanceExists
                      ? "I'm analyzing your performance now. This could take a few minutes to complete."
                      : "Ready to see how good your performance is? Submit your recording to get scored!"}
                  </div>
                  {analysisExists && coachingResponse && !analysisError && (
                    <button
                      onClick={() => setIsExpanded(!isExpanded)}
                      className="text-kod-accent text-xs mt-1 hover:text-kod-accent-dark transition-colors"
                    >
                      {isExpanded ? 'Show less' : 'Read more'}
                    </button>
                  )}
                </div>
                {performanceExists && !analysisExists && !analysisError && (
                  <p className="text-white/70 text-sm mt-2">
                    Feel free to close this window and continue using the app. You'll be notified when your score is ready!
                  </p>
                )}
              </div>
            </div>
          </div>
          
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}
          
          {!isExpanded && (
            <div className="bg-kod-purple-700/30 rounded-lg p-4 mb-6 border border-kod-purple-600/30">
              {analysisError ? (
                <div className="text-center py-8">
                  <div className="relative mb-4">
                    <div className="absolute inset-0 flex items-center justify-center opacity-10">
                      <AlertCircle size={120} className="text-red-400" />
                    </div>
                    <div className="relative z-10">
                      <AlertCircle size={56} className="mx-auto text-red-400 mb-3" />
                      <h3 className="text-xl font-bold text-red-400 mb-2">Analysis Error</h3>
                      <div className="inline-block bg-red-500/20 border border-red-500/30 rounded-lg px-4 py-2 mb-3">
                        <p className="text-red-300 font-mono text-sm">Error Code: {analysisError.errorCode}</p>
                      </div>
                      <div className="bg-kod-purple-800/50 rounded-lg p-3 mx-auto max-w-sm">
                        <p className="text-white/70 text-sm leading-relaxed">
                          {analysisError.message}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="text-center mb-4">
                    <div className="py-2">
                      {analysisExists ? (
                        <>
                          <div className="text-5xl font-bold text-kod-accent">{overallScore.toFixed(1)}</div>
                          <p className="text-white/70 text-sm mt-1">OVERALL SCORE</p>
                        </>
                      ) : performanceExists ? (
                        <div className="text-center py-6">
                          <Loader className="w-10 h-10 mx-auto animate-spin mb-4 text-kod-accent" />
                          <p className="text-lg font-semibold text-white">{progressMsg}</p>
                          <p className="text-sm text-white/50 mt-2">This can take a few minutes.</p>
                          <div className="mt-6 max-w-xs mx-auto">
                            <div className="flex gap-1 h-2 rounded-full overflow-hidden bg-white/10">
                              {[0, 1, 2, 3].map((i) => (
                                <div
                                  key={i}
                                  className="flex-1 rounded-full transition-all duration-700"
                                  style={{
                                    backgroundColor: i <= progressStep ? '#f59e0b' : 'transparent',
                                    opacity: i < progressStep ? 1 : i === progressStep ? 0.5 : 0,
                                  }}
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          <BarChart2 size={48} className="mx-auto text-kod-accent/50 mb-2" />
                          <div className="text-white/70 text-lg">Submit your recording to get scored</div>
                        </>
                      )}
                    </div>
                  </div>

                  {analysisExists && (
                    <div className="space-y-4">
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-white/70">Pitch Accuracy</span>
                          <span className="font-medium">{pitchAccuracy.toFixed(1)}</span>
                        </div>
                        <div className="h-2 bg-kod-purple-600 rounded-full">
                          <div
                            className="h-2 bg-kod-accent rounded-full"
                            style={{ width: `${pitchAccuracy}%` }}
                          ></div>
                        </div>
                      </div>

                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-white/70">Rhythm & Timing</span>
                          <span className="font-medium">{rhythmAccuracy.toFixed(1)}</span>
                        </div>
                        <div className="h-2 bg-kod-purple-600 rounded-full">
                          <div
                            className="h-2 bg-green-500"
                            style={{ width: `${rhythmAccuracy}%` }}
                          ></div>
                        </div>
                      </div>

                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-white/70">Style/Delivery</span>
                          <span className="font-medium">{styleScore.toFixed(1)}</span>
                        </div>
                        <div className="h-2 bg-kod-purple-600 rounded-full">
                          <div
                            className="h-2 bg-yellow-500"
                            style={{ width: `${styleScore}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          
          <div className="grid grid-cols-2 gap-3">
            {showRetry ? (
              <button
                onClick={() => {
                  setAnalysisExists(false);
                  setAnalysisError(null);
                  setPerformanceExists(false);
                  setError(null);
                  handleScorePerformance();
                }}
                className="flex items-center justify-center bg-kod-accent hover:bg-kod-accent-dark text-white py-3 px-4 rounded-lg transition-colors"
                disabled={isSubmitting}
              >
                <BarChart2 size={18} className="mr-2" />
                {isSubmitting ? 'Retrying...' : 'Retry Scoring'}
              </button>
            ) : isTournament && (analysisExists || analysisError) ? (
              <button
                onClick={onClose}
                className="flex items-center justify-center bg-kod-accent hover:bg-kod-accent-dark text-white py-3 px-4 rounded-lg transition-colors"
              >
                Back to Tournament
              </button>
            ) : isTournament ? (
              <button
                disabled
                className="flex items-center justify-center bg-kod-purple-600/70 text-white/70 py-3 px-4 rounded-lg cursor-not-allowed"
              >
                Scoring In Progress
              </button>
            ) : uploadError ? (
              <button
                onClick={onClose}
                className="flex items-center justify-center bg-kod-accent hover:bg-kod-accent-dark text-white py-3 px-4 rounded-lg transition-colors"
              >
                Close
              </button>
            ) : analysisExists ? (
            <button
              onClick={handleTryAnotherSong}
              className="flex items-center justify-center bg-kod-purple-700 hover:bg-kod-purple-600 text-white py-3 px-4 rounded-lg transition-colors"
            >
              Try Another Song
            </button>
            ) : performanceExists ? (
              <button
                disabled
                className="flex items-center justify-center bg-kod-purple-600/70 text-white/70 py-3 px-4 rounded-lg cursor-not-allowed"
              >
                Scoring In Progress
              </button>
            ) : (
              <button
                onClick={handleScorePerformance}
                className="flex items-center justify-center bg-kod-accent hover:bg-kod-accent-dark text-white py-3 px-4 rounded-lg transition-colors"
                disabled={isSubmitting}
              >
                <BarChart2 size={18} className="mr-2" />
                Score Performance
              </button>
            )}
            
            <button
              onClick={() => setBlueprintModalOpen(true)}
              disabled={!analysisExists || !reportCompressedData || isSubmitting}
              className={`flex items-center justify-center ${
                !analysisExists || !reportCompressedData || isSubmitting
                  ? 'bg-kod-purple-600 cursor-not-allowed text-white/50'
                  : 'bg-kod-accent hover:bg-kod-accent-dark text-white'
              } py-3 px-4 rounded-lg transition-colors`}
            >
              <FileText size={18} className="mr-2" />
              Vocal Blueprint
            </button>
          </div>
          
          <button
            className="w-full mt-3 flex items-center justify-center bg-kod-purple-700/50 hover:bg-kod-purple-700 text-white py-3 px-4 rounded-lg transition-colors"
            disabled
          >
            <Users size={18} className="mr-2" />
            Join a League (Coming Soon)
          </button>
        </div>
      </div>

      <VocalBlueprintModal
        isOpen={blueprintModalOpen}
        onClose={() => setBlueprintModalOpen(false)}
        compressedData={reportCompressedData}
        title={`Vocal Blueprint: ${songTitle} - ${songArtist}`}
        lyricsUrl={lyricsUrl}
        vocalRecordingUrl={vocalsOnlyUrl || userRecordingUrl}
        leadVocalUrl={leadVocalUrlForBlueprint || originalVocalUrl}
        finalRecordingUrl={mixedRecordingUrl}
      />
    </div>
  );
};